import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, CalendarDays, Loader2, RefreshCw, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import HolidayCalendarDialog from '@/components/premium/HolidayCalendarDialog';
import TodayTab from '@/components/premium/planner/TodayTab';
import WeekTab from '@/components/premium/planner/WeekTab';
import RoadmapTab from '@/components/premium/planner/RoadmapTab';
import SubjectsTab from '@/components/premium/planner/SubjectsTab';
import SettingsTab from '@/components/premium/planner/SettingsTab';
import { addDays, prettyMinutes } from '@/lib/plannerUtils';
import { apiGet, apiPost, apiPut } from '@/lib/api';

const TABS = [
  ['today', 'Today'],
  ['week', 'Week'],
  ['roadmap', 'Roadmap'],
  ['subjects', 'Subjects'],
  ['settings', 'Settings'],
];

const RISK_BANNER = {
  orange: 'border-amber-300 bg-amber-50 text-amber-900',
  red: 'border-rose-300 bg-rose-50 text-rose-800',
};

/**
 * The Study / Exam Planner. Lives inline on the Exams page (where it can be
 * rebuilt) and on its own page from the sidebar (where it is read-and-tick
 * only) — same component, so the two can never drift apart.
 */
export function StudyPlanner({ childId = '', canManage = false, examId = '', onPlanChange }) {
  const [exams, setExams] = useState([]);
  const [selected, setSelected] = useState(examId);
  const [plan, setPlan] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [tab, setTab] = useState('today');
  const [cursor, setCursor] = useState('');
  const [weekAnchor, setWeekAnchor] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [holidaysOpen, setHolidaysOpen] = useState(false);

  const exam = useMemo(() => exams.find(e => e.exam_id === selected) || null, [exams, selected]);

  const loadExams = useCallback(async () => {
    const q = childId ? `?child_id=${encodeURIComponent(childId)}` : '';
    const res = await apiGet(`/premium/v1/exams${q}`);
    const live = (res.exams || []).filter(e => e.status !== 'archived');
    setExams(live);
    setSelected(prev => (live.some(e => e.exam_id === prev) ? prev : (live[0]?.exam_id || '')));
    return live;
  }, [childId]);

  const loadPlan = useCallback(async (id) => {
    if (!id) { setPlan(null); return null; }
    const res = await apiGet(`/premium/v1/exams/${id}/plan`);
    setPlan(res.plan || null);
    onPlanChange?.(res.plan || null);
    return res.plan || null;
  }, [onPlanChange]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [, pr] = await Promise.all([
          loadExams(),
          apiGet(`/premium/v1/study-prefs${childId ? `?child_id=${encodeURIComponent(childId)}` : ''}`),
        ]);
        setPrefs(pr);
      } catch (e) { toast.error(e?.message || 'Could not load the planner'); }
      setLoading(false);
    })();
  }, [loadExams, childId]);

  useEffect(() => { if (selected) loadPlan(selected).catch(() => {}); }, [selected, loadPlan]);

  useEffect(() => {
    if (!plan) return;
    // Today is often a Sunday or a holiday — land on the next planned day so the
    // student always opens onto something to do, never a blank screen.
    const next = (plan.days || []).find(d => d.date >= plan.today && d.items.length)?.date;
    setCursor(c => c || next || plan.today);
    setWeekAnchor(a => a || next || plan.today);
  }, [plan]);

  const day = useMemo(() => (plan?.days || []).find(d => d.date === cursor) || null, [plan, cursor]);

  const refreshExam = async () => {
    const live = await loadExams();
    if (live.length) await loadPlan(selected || live[0].exam_id);
  };

  const toggleDone = async (ref, done) => {
    try {
      const res = await apiPost(`/premium/v1/plans/${plan.plan_id}/items/done`, { ref, done });
      setPlan(res);
      if (res.streak?.awarded) toast.success(`${res.streak.current}-day streak — ${res.streak.awarded} Veda Coins!`);
    } catch (e) { toast.error(e?.message || 'Could not update that'); }
  };

  const notToday = async (date) => {
    setBusy(true);
    try { setPlan(await apiPost(`/premium/v1/plans/${plan.plan_id}/day/${date}/not-today`, {})); }
    catch (e) { toast.error(e?.message || 'Could not reshuffle'); }
    setBusy(false);
  };

  const rebuild = async () => {
    setBusy(true);
    try {
      setPlan(await apiPost(`/premium/v1/exams/${selected}/plan`, {}));
      toast.success('Plan rebuilt around your settings');
    } catch (e) { toast.error(e?.message || 'Could not build the plan'); }
    setBusy(false);
  };

  const setConfidence = async (subject, chapter, level) => {
    try {
      const updated = await apiPut(`/premium/v1/exams/${selected}/confidence`, { subject, chapter, level });
      setExams(prev => prev.map(e => (e.exam_id === updated.exam_id ? updated : e)));
      await loadPlan(selected);
      toast.message(level === 'weak' ? `${chapter} gets 30 minutes more`
        : level === 'strong' ? `${chapter} trimmed a little` : `${chapter} back to normal time`);
    } catch (e) { toast.error(e?.message || 'Could not save that'); }
  };

  const savePaperDates = async (dates) => {
    setBusy(true);
    try {
      const updated = await apiPut(`/premium/v1/exams/${selected}/paper-dates`, { dates });
      setExams(prev => prev.map(e => (e.exam_id === updated.exam_id ? updated : e)));
      await loadPlan(selected);
      toast.success('Paper dates saved — the plan now works around them');
    } catch (e) { toast.error(e?.message || 'Could not save those dates'); }
    setBusy(false);
  };

  const savePref = async (key, value) => {
    try {
      const res = await apiPut('/premium/v1/study-prefs', {
        child_id: childId || null, daily_minutes: prefs.daily_minutes, weekly_off: prefs.weekly_off,
        buffer_pct: prefs.buffer_pct, revision_days: prefs.revision_days ?? null,
        mocks_enabled: prefs.mocks_enabled, full_portion_revision: prefs.full_portion_revision,
        puzzle_slots: prefs.puzzle_slots, [key]: value,
      });
      setPrefs(res);
      if (selected) { setPlan(await apiPost(`/premium/v1/exams/${selected}/plan`, {})); }
      toast.message('Saved — the plan has been reshaped around it');
    } catch (e) { toast.error(e?.message || 'Could not save that'); }
  };

  if (loading) {
    return (
      <p className="py-10 text-center text-sm text-slate-400 font-body" data-testid="planner-loading">
        <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading your planner…
      </p>
    );
  }

  if (!exams.length) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center" data-testid="planner-no-exam">
        <CalendarDays className="w-6 h-6 text-slate-300 mx-auto" />
        <p className="font-heading text-base text-slate-800 mt-2">No exam to plan yet</p>
        <p className="font-body text-[13px] text-slate-500 mt-1">
          Add an exam and map its portion on the Exams page — the planner fills itself in from there.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="study-planner">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h2 className="font-heading text-xl sm:text-2xl font-bold text-slate-900 truncate">
            {exam?.name || 'Study planner'}
          </h2>
          <p className="font-body text-xs md:text-sm text-slate-500">
            {exam?.assessment_label}{exam?.start_date_ist ? ` · ${exam.start_date_ist} – ${exam.end_date_ist}` : ''}
          </p>
        </div>
        <div className="text-right">
          {plan?.days_to_exam !== null && plan?.days_to_exam !== undefined && (
            <p className="font-heading text-2xl font-bold text-slate-900" data-testid="planner-countdown">
              {Math.max(plan.days_to_exam, 0)}
              <span className="ml-1 font-body text-[11px] text-slate-400">days to the first paper</span>
            </p>
          )}
          {plan && (
            <p className="font-body text-[11px] text-slate-400" data-testid="planner-pace">
              {prettyMinutes(plan.per_day_target)} a day · {plan.items_done}/{plan.items_total} done
            </p>
          )}
        </div>
      </div>

      {exams.length > 1 && (
        <div className="flex gap-1.5 flex-wrap mt-2" data-testid="planner-exam-switch">
          {exams.map(e => (
            <button key={e.exam_id} onClick={() => setSelected(e.exam_id)}
              data-testid={`planner-exam-${e.exam_id}`}
              className={`px-2.5 py-1 rounded-full text-[11px] font-heading border transition-colors ${selected === e.exam_id ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
              {e.name}
            </button>
          ))}
        </div>
      )}

      <nav className="flex gap-4 border-b border-slate-200 mt-4 overflow-x-auto" data-testid="planner-tabs">
        {TABS.map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`planner-tab-${key}`}
            className={`pb-2 font-heading text-sm transition-colors border-b-2 -mb-px whitespace-nowrap ${
              tab === key ? 'border-indigo-500 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            {label}
          </button>
        ))}
      </nav>

      {plan?.pace_note && !plan?.portion_at_risk && (
        <p className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 p-3 font-body text-[12px] text-slate-600"
          data-testid="planner-pace-note">{plan.pace_note}</p>
      )}

      {plan?.risk && plan.risk !== 'green' && (
        <div className={`rounded-2xl border p-3 mt-3 ${RISK_BANNER[plan.risk]}`} data-testid={`planner-risk-${plan.risk}`}>
          <p className="flex items-start gap-2 text-[12px] font-body leading-relaxed">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {plan.risk_message}
          </p>
        </div>
      )}

      {canManage && (
        <div className="flex flex-wrap gap-2 mt-3" data-testid="planner-manage">
          <Button size="sm" className="h-9 text-[11px] bg-gradient-to-r from-indigo-500 to-violet-500 text-white"
            disabled={busy} data-testid="planner-rebuild-btn" onClick={rebuild}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
              : plan ? <RefreshCw className="w-3.5 h-3.5 mr-1" /> : <Wand2 className="w-3.5 h-3.5 mr-1" />}
            {plan ? 'Reset and rebuild the plan' : 'Build the plan'}
          </Button>
          <Button size="sm" variant="outline" className="h-9 text-[11px]" onClick={refreshExam}
            data-testid="planner-refresh-btn">Refresh</Button>
        </div>
      )}

      <div className="mt-4">
        {!plan && tab !== 'settings' && tab !== 'subjects' && (
          <p className="py-8 text-center font-body text-[13px] text-slate-500" data-testid="planner-empty">
            No plan built for this exam yet.{canManage ? ' Press “Build the plan”.' : ' Build it from the Exams page.'}
          </p>
        )}
        {plan && tab === 'today' && (
          <TodayTab plan={plan} day={day} cursor={cursor} onToggleDone={toggleDone}
            onCursor={(delta) => setCursor(delta === 0 ? plan.today : addDays(cursor, delta))}
            onJump={setCursor} />
        )}
        {plan && tab === 'week' && (
          <WeekTab plan={plan} anchor={weekAnchor} onToggleDone={toggleDone}
            onShift={(delta) => setWeekAnchor(delta === 0 ? plan.today : addDays(weekAnchor, delta))} />
        )}
        {plan && tab === 'roadmap' && (
          <RoadmapTab plan={plan} onToggleDone={toggleDone} onNotToday={notToday} canManage={canManage} />
        )}
        {tab === 'subjects' && <SubjectsTab exam={exam} plan={plan} onConfidence={setConfidence} />}
        {tab === 'settings' && (
          <SettingsTab exam={exam} prefs={prefs} saving={busy} onPref={savePref} onPaperDates={savePaperDates}
            onHolidays={() => setHolidaysOpen(true)} />
        )}
      </div>

      <HolidayCalendarDialog open={holidaysOpen} childId={childId}
        onOpenChange={(o) => { setHolidaysOpen(o); if (!o && selected) loadPlan(selected); }} />
    </div>
  );
}

export default StudyPlanner;
